import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Loader2, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSecurityPulse } from "../hooks/useSecurityPulse";
import { Button } from "./ui/button";

const inr = (n) => `₹${Number(Math.round(n) || 0).toLocaleString("en-IN")}`;

/**
 * PayFromWalletButton — reusable green pill that pays an invoice from
 * the user's Vinci Wallet balance after a mandatory fresh Security
 * Pulse OTP (`wallet_debit`). Silently hides when the user is unauthed
 * or the invoice is missing. Renders as either:
 *   • Green "Pay ₹X from Wallet" when balance is sufficient
 *   • Grey "Insufficient Wallet · ₹X short" hint when not
 */
export function PayFromWalletButton({ invoice, onPaid, testIdPrefix = "pfw",
                                        className = "" }) {
  const { user, api } = useAuth();
  const pulse = useSecurityPulse(api);
  const [balance, setBalance] = useState(null);
  const [floor, setFloor] = useState(500);
  const [restriction, setRestriction] = useState(null);
  const [busy, setBusy] = useState(false);
  const authed = !!(user && typeof user === "object" && user.email);
  const amount = Number(invoice?.total || invoice?.amount || 0);
  const inv_id = invoice?.id || invoice?.invoice_id;

  const loadBalance = useCallback(async () => {
    if (!authed) return;
    try {
      const { data } = await api.get("/wallet/balance");
      setBalance(Number(data.balance || 0));
      setRestriction(data.payment_restriction || null);
      if (data.ecosystem_floor !== undefined)
        setFloor(Number(data.ecosystem_floor));
    } catch { setBalance(0); }
  }, [authed, api]);

  useEffect(() => { loadBalance(); }, [loadBalance]);

  if (!authed || !inv_id || amount <= 0 || balance === null) return null;
  if (restriction?.active) {
    return (
      <div data-testid="pay-wallet-restricted" className="rounded-sm border border-[#C0392B]/60 bg-[#C0392B]/10 px-4 py-3 text-sm text-[#F5F5F0]">
        <div className="font-grotesk text-[11px] uppercase tracking-[0.2em] text-[#C0392B]">Wallet unavailable as a payment source</div>
        <div className="mt-1">{restriction.reason}</div>
      </div>
    );
  }
  const remaining = balance - amount;
  const hasFunds = balance >= amount;
  const keepsFloor = remaining >= floor;
  const sufficient = hasFunds && keepsFloor;
  // "short" is how much the buyer needs to top up so the debit both
  // clears the invoice AND leaves the ₹500 ecosystem floor intact.
  const short = Math.max(0, (amount + floor) - balance);

  const run = async () => {
    if (!hasFunds) {
      toast.warning(`Top up ${inr(short)} first — Vinci Wallet has ${inr(balance)}.`);
      return;
    }
    if (!keepsFloor) {
      toast.warning(
        `Blocked · this payment would leave ${inr(remaining)} — Vinci accounts `
        + `need to keep a ${inr(floor)} floor to stay trade-active. `
        + `Top up ${inr(short)} first.`
      );
      return;
    }
    setBusy(true);
    try {
      const tok = await pulse.request("wallet_debit", inv_id);
      if (!tok) { setBusy(false); return; }
      const { data } = await api.post("/wallet/pay-invoice",
        { invoice_id: inv_id },
        { headers: { "X-Security-Pulse": tok } });
      toast.success(
        `Paid ${inr(amount)} · New wallet balance ${inr(data.balance_after)} · `
        + `${data.invoice_number || "invoice"} settled.`
      );
      setBalance(data.balance_after);
      onPaid && onPaid(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Wallet payment failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      {pulse.modal}
      <Button
        type="button"
        onClick={run} disabled={busy || !sufficient}
        data-testid={`${testIdPrefix}-${inv_id}`}
        title={sufficient
                ? (`Debit ${inr(amount)} from your Vinci Wallet with an OTP. `
                    + `Balance after: ${inr(remaining)} (floor ${inr(floor)}).`)
                : !hasFunds
                  ? `Top-up ${inr(short)} first — Vinci Wallet has ${inr(balance)}.`
                  : `Blocked · would leave ${inr(remaining)}. Vinci ecosystem `
                    + `floor is ${inr(floor)}. Top up ${inr(short)} more.`}
        className={
          "font-grotesk uppercase tracking-[0.22em] text-[10px] rounded-sm "
          + "px-4 py-2.5 inline-flex items-center gap-1.5 shadow-lg transition-all "
          + (sufficient
              ? "bg-gradient-to-r from-[#0f7d5c] via-[#2FB999] to-[#0f7d5c] "
                + "hover:brightness-110 text-black "
                + "hover:shadow-[0_0_30px_-8px_#2FB999]"
              : "bg-black/30 border border-white/15 text-[#8A8A82] "
                + "hover:text-[#C9C9C2] hover:border-white/30 cursor-not-allowed")
          + " " + className
        }
      >
        {busy ? (<><Loader2 size={12} className="animate-spin" /> Debiting…</>) :
          sufficient ? (
            <><Sparkles size={12} /> Pay {inr(amount)} from Wallet</>
          ) : !hasFunds ? (
            <><Wallet size={12} /> Wallet short by {inr(short)}</>
          ) : (
            <><Wallet size={12} /> Keeps only {inr(remaining)} · need {inr(floor)} floor</>
          )}
      </Button>
    </>
  );
}
